import { useId } from 'react';
import PropTypes from 'prop-types';

import InfoTooltip from '@/components/tooltip/InfoTooltip.jsx';

import './Field.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* Ties the label, the control and the message together for assistive tech. The control owns the
   id so it can also be the <label for> target; the message id is derived from it. */
export const useFieldIds = (id, hasMessage) => {
    const auto = useId();
    const fieldId = id || auto;
    return { fieldId, messageId: hasMessage ? `${fieldId}-message` : undefined };
};

/* Enter releases the field: commits what was typed, closes anything open, moves focus nowhere.
   preventDefault stops a surrounding <form> from submitting behind it. Not for <textarea>. */
export const blurOnEnter = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.currentTarget.blur();
};

/**
 * The shell every form control sits in: label, required marker, tooltip, and one message line.
 *
 * Status is the only colour switch — `danger` repaints the label, the message, the border and the
 * control's own text and placeholder by reassigning five custom properties that every part reads.
 * There is no separate `error` prop: pass the message as `helpText` and set `status="danger"`.
 *
 * Choice controls (checkbox, radio, toggle) put their label beside the control instead of above it,
 * so they render their own `.field-label` inside their `<label>` and leave this one unset.
 *
 * Disabled is an opacity change, not a colour change — a disabled field in danger stays red, just
 * dimmed.
 */
const Field = ({
    id,
    label,
    labelEnd,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    tooltip,
    className = '',
    children,
}) => (
    <div
        className={cx(
            'field',
            status === 'danger' && 'field--danger',
            isDisabled && 'field--disabled',
            className,
        )}
    >
        {(label || labelEnd) && (
            <div className="field-labelrow">
                {label && (
                    <label className="field-label" htmlFor={id}>
                        {label}
                        {isRequired && <span className="field-required">*</span>}
                        {tooltip && <InfoTooltip text={tooltip} />}
                    </label>
                )}
                {/* Outside the <label> on purpose: clicking an interactive end slot must not
                    focus the control the way clicking the label text does. */}
                {labelEnd && <span className="field-labelend">{labelEnd}</span>}
            </div>
        )}
        {children}
        {helpText && <span className="field-message" id={`${id}-message`}>{helpText}</span>}
    </div>
);

Field.propTypes = {
    id: PropTypes.string,
    label: PropTypes.node,
    // Trailing slot on the label line — an icon, a count, a small action.
    labelEnd: PropTypes.node,
    // The one message line. Reads as help text when neutral, as the error when danger.
    helpText: PropTypes.node,
    status: PropTypes.oneOf(['neutral', 'danger']),
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    tooltip: PropTypes.string,
    className: PropTypes.string,
    /* No `children` validator: Select and Autocomplete pass a createPortal result alongside the
       trigger, and PropTypes.node rejects portals. */
};

export default Field;
